import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LuSearch } from 'react-icons/lu';

import useModuleCatalog from '../../hooks/useModuleCatalog';
import useRecentScreens from '../../hooks/useRecentScreens';
import { useModule } from '../../components/Menu/ModuleContext';

import {
   HomeContainer,
   Greeting,
   SearchWrap,
   SearchField,
   SearchInput,
   SearchHint,
   SectionLabel,
   ModuleGrid,
   ModulesBlock,
   EmptyState,
} from './styles';
import { searchItems, MIN_QUERY_LENGTH } from './search';
import ModuleCard from './components/ModuleCard';
import ModuleModal from './components/ModuleModal';
import SearchResults from './components/SearchResults';
import RecentChips from './components/RecentChips';

/**
 * Home — porta de entrada do MWA (handoff `design_handoff_home`).
 *
 * Navegação faseada em dois níveis: a home mostra apenas os módulos; os itens
 * de menu aparecem em modal. A busca é o atalho de quem já sabe o destino.
 *
 * A tela não faz nenhuma chamada de API — renderiza a partir da mesma árvore de
 * permissões que alimenta a sidebar (`useModuleCatalog`).
 */
function greetingFor(hour) {
   if (hour < 12) return 'Bom dia';
   if (hour < 18) return 'Boa tarde';
   return 'Boa noite';
}

export default function Main() {
   const user = useSelector((state) => state.auth.user);
   const { modules, flatItems } = useModuleCatalog();
   const { recents } = useRecentScreens();
   const { selectModule } = useModule();

   const [query, setQuery] = useState('');
   const [openModule, setOpenModule] = useState(null);
   const searchRef = useRef(null);

   const greeting = useMemo(() => greetingFor(new Date().getHours()), []);
   const firstName = (user?.nome || '').trim().split(/\s+/)[0];

   // -------------------------------------------------------------------------
   // Busca (§2.3 + ajustes de 2026-08-22): casa o rótulo do item OU o nome do
   // módulo, sobre a árvore já filtrada por permissão — item sem permissão não
   // aparece nem nos resultados.
   //
   // Só dispara a partir de MIN_QUERY_LENGTH caracteres: com 1–2 letras quase
   // todo o catálogo casaria, e a grade de módulos some por nada.
   // -------------------------------------------------------------------------
   const { results, total, isActive: isSearching } = useMemo(
      () => searchItems(flatItems, query),
      [flatItems, query]
   );

   const typedTooShort =
      query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH;

   // -------------------------------------------------------------------------
   // Ctrl/Cmd+K foca a busca (§2.3). O Esc do modal é do primitivo `Modal`.
   // -------------------------------------------------------------------------
   useEffect(() => {
      const onKeyDown = (e) => {
         if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            searchRef.current?.focus();
            searchRef.current?.select();
         }
      };

      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
   }, []);

   // Sincroniza o módulo ativo da sidebar antes de sair da home, para o usuário
   // chegar na tela com o menu já no módulo certo. Configurações não é módulo
   // (`isModule: false`) — não mexe na seleção.
   const syncActiveModule = useCallback(
      (moduleId, isModule) => {
         if (isModule !== false && moduleId) selectModule(moduleId);
      },
      [selectModule]
   );

   const handleOpenModule = useCallback((mod) => setOpenModule(mod), []);
   const handleCloseModule = useCallback(() => setOpenModule(null), []);

   const handleNavigateFromModal = useCallback(() => {
      if (openModule) syncActiveModule(openModule.id, openModule.isModule);
      setOpenModule(null);
   }, [openModule, syncActiveModule]);

   const handleNavigateFromList = useCallback(
      (item) => syncActiveModule(item.moduleId, item.isModule),
      [syncActiveModule]
   );

   const hasModules = modules.length > 0;

   return (
      <HomeContainer>
         <Greeting>
            <h1>
               {greeting}
               {firstName ? `, ${firstName}` : ''}
            </h1>
            <p>Escolha um módulo para começar ou busque o menu desejado.</p>
         </Greeting>

         {hasModules && (
            <SearchWrap>
               {/* A lupa e o chip ancoram no CAMPO, não no bloco: o aviso
                   abaixo pode aparecer sem empurrá-los para baixo. */}
               <SearchField $typing={query.length > 0}>
                  <LuSearch className="search-icon" size={18} />
                  <SearchInput
                     ref={searchRef}
                     type="text"
                     value={query}
                     onChange={(e) => setQuery(e.target.value)}
                     placeholder="Pesquisar menu"
                     aria-label="Pesquisar menu"
                     aria-describedby="search-min-length-hint"
                  />
                  <span className="search-hint" aria-hidden="true">
                     Ctrl K
                  </span>
               </SearchField>

               {/* Sempre montado: o colapso é animado por CSS (0fr → 1fr), o
                   que dá transição de altura de verdade. `aria-live` faz o
                   leitor de tela anunciar o aviso ao surgir. */}
               <SearchHint $visible={typedTooShort} aria-hidden={!typedTooShort}>
                  <p id="search-min-length-hint" role="status" aria-live="polite">
                     {typedTooShort
                        ? `Digite ao menos ${MIN_QUERY_LENGTH} caracteres para pesquisar.`
                        : ''}
                  </p>
               </SearchHint>
            </SearchWrap>
         )}

         {!hasModules && (
            <EmptyState>
               <h3>Nenhum módulo liberado</h3>
               <p>
                  Seu perfil de acesso ainda não tem permissões para nenhum módulo
                  do sistema. Fale com o administrador para liberar o acesso.
               </p>
            </EmptyState>
         )}

         {hasModules && isSearching && (
            <SearchResults
               results={results}
               total={total}
               onNavigate={handleNavigateFromList}
            />
         )}

         {hasModules && !isSearching && (
            <>
               <ModulesBlock>
                  <SectionLabel>Módulos</SectionLabel>
                  <ModuleGrid>
                     {modules.map((mod) => (
                        <ModuleCard
                           key={mod.id}
                           module={mod}
                           onOpen={handleOpenModule}
                        />
                     ))}
                  </ModuleGrid>
               </ModulesBlock>

               <RecentChips
                  recents={recents}
                  onNavigate={handleNavigateFromList}
               />
            </>
         )}

         <ModuleModal
            module={openModule}
            isOpen={!!openModule}
            onClose={handleCloseModule}
            onNavigate={handleNavigateFromModal}
         />
      </HomeContainer>
   );
}
